import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '@app/pages/dashboard/initiative/shared/workflow'
import { CreationStepLayout } from '../blocks/CreationStepLayout'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { useClient } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import { useToast } from '@app/util/hooks/useToast'
import type { InitiativeWithId } from '@app/pages/dashboard/initiative/shared/initiativeTypes'
import { InitiativeImageEditor } from '@app/components/blocks/initiativeImageEditor/InitiativeImageEditor'
import { type ImagePreview } from '@app/components/blocks/imageEditRow/ImageEditRow'

const CURRENT_STEP = initiativeCreationSteps.images

export const ImagesStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
}) => {
  const translations = useTranslations().app.dashboard.initiative.creation
  const client = useClient()
  const { showFailToast } = useToast()
  const [addedImages, setAddedImages] = useState<ImagePreview[]>([])
  const [removedImages, setRemovedImages] = useState<number[]>([])
  const uploadingPreviewsRef = useRef(new Set<string>())
  const isDeletingRef = useRef(false)

  const uploadImage = useCallback(
    async (preview: ImagePreview) => {
      if (uploadingPreviewsRef.current.has(preview.preview)) {
        return
      }

      uploadingPreviewsRef.current.add(preview.preview)

      try {
        await client.stream('upload-file', {
          mimeType: preview.file.type,
          contents: preview.file,
          size: preview.file.size,
          fileName: `initiative-media-${Date.now()}-${preview.file.name}`,
          payload: { initiativeImage: { id: initiative.id } },
        })
      } catch {
        showFailToast()
      } finally {
        uploadingPreviewsRef.current.delete(preview.preview)
        URL.revokeObjectURL(preview.preview)
        setAddedImages((prev) =>
          prev.filter((image) => image.preview !== preview.preview),
        )
      }
    },
    [client, initiative.id, showFailToast],
  )

  const setNewImages = useCallback<Dispatch<SetStateAction<ImagePreview[]>>>(
    (action) => {
      setAddedImages((prev) => {
        const next = typeof action === 'function' ? action(prev) : action
        const newlyAdded = next.filter(
          (image) =>
            !prev.some((existing) => existing.preview === image.preview),
        )

        newlyAdded.forEach((image) => {
          void uploadImage(image)
        })

        return next
      })
    },
    [uploadImage],
  )

  useEffect(() => {
    completeStep(CURRENT_STEP)
  }, [completeStep])

  useEffect(() => {
    client
      .call('edit-initiative', {
        id: initiative.id,
        value: { onboardingStep: CURRENT_STEP },
      })
      .catch(() => showFailToast())
  }, [client, initiative.id, showFailToast])

  useEffect(() => {
    if (removedImages.length === 0 || isDeletingRef.current) {
      return
    }

    isDeletingRef.current = true
    const fileIdsToRemove = [...removedImages]

    Promise.all(
      fileIdsToRemove.map((fileId) => client.call('delete-file', { fileId })),
    )
      .then(() => setRemovedImages([]))
      .catch(() => showFailToast())
      .finally(() => {
        isDeletingRef.current = false
      })
  }, [client, removedImages, showFailToast])

  return (
    <CreationStepLayout
      selectedPhaseType={selectedPhaseType}
      currentStep={CURRENT_STEP}
    >
      <InitiativeImageEditor
        existingImages={initiative.images ?? []}
        newImages={addedImages}
        removedImages={removedImages}
        infoMessage={translations.imagesInfoAlert}
        bannerLabel={translations.bannerImageLabel}
        uploadBannerLabel={translations.uploadNewBannerImage}
        setNewImages={setNewImages}
        setRemovedImages={setRemovedImages}
      />
    </CreationStepLayout>
  )
}
